import { useId, useState } from 'react'

import type { LearningRank, ToughReason } from '../../api/flashcards'
import { Icon, type IconName } from '../../ui/Icon'

/**
 * How one direction of a card stands, in the four states worth telling apart.
 *
 * One state per direction, not four numbers to correlate: a colour and a picture say it at a glance,
 * and the sentences behind them are one press away. The order below is the order of urgency, and the
 * first that fits wins.
 */
export type CardStanding = 'rote' | 'shaky' | 'solid' | 'known'

export function standingOf(rank: LearningRank): CardStanding {
  if (rank.toughReasons.length > 0) return 'rote'
  if (rank.assessedAttempts === 0) return 'known'
  if (rank.learningProgress < LOW_PROGRESS || rank.incorrectAttempts > 0) return 'shaky'
  return 'solid'
}

/** True when at least one direction of this card is asking for work. */
export function needsWork(ranks: readonly LearningRank[]): boolean {
  return ranks.some((rank) => standingOf(rank) === 'rote' || standingOf(rank) === 'shaky')
}

const LOW_PROGRESS = 45

const STANDING: Readonly<
  Record<CardStanding, { readonly icon: IconName; readonly label: string }>
> = {
  rote: { icon: 'rote', label: 'Needs learning by heart' },
  shaky: { icon: 'warning', label: 'Shaky' },
  solid: { icon: 'check', label: 'Sitting well' },
  known: { icon: 'blank', label: 'Not asked yet' },
}

const DIRECTION: Readonly<Record<LearningRank['direction'], string>> = {
  forward: 'Front to back',
  reverse: 'Back to front',
}

export const TOUGH_REASON_COPY: Record<ToughReason, string> = {
  never_answered: 'Never answered yet — nothing to retrieve so far',
  no_clue_when_asked: 'You had no clue when it came up in a round',
  recent_failures: 'Recent uncertainty',
  repeated_failure_across_runs: 'Missed repeatedly across several rounds',
  progress_stuck: 'Learning progress stays near the floor',
  relapse_after_relearn: 'Relearned in a round, then missed again later',
}

/**
 * The state of a card, per direction, as one chip each.
 *
 * Pressing a chip says the rest in words — the numbers behind the colour and, where MyLib counts the
 * card as one to learn by heart, the named reasons for it. Pressing rather than hovering only, because
 * a finger cannot hover; the same text is the chip's accessible name, so it is never only a colour.
 */
export function CardStatus({ ranks }: { readonly ranks: readonly LearningRank[] }) {
  const [open, setOpen] = useState<string | null>(null)
  const panelId = useId()

  if (ranks.length === 0) return null

  return (
    <div className="card-standing">
      <div className="card-standing__chips">
        {ranks.map((rank) => {
          const standing = standingOf(rank)
          const shown = open === rank.direction
          return (
            <button
              type="button"
              key={rank.direction}
              className={`standing standing--${standing}${shown ? ' standing--open' : ''}`}
              title={detailsOf(rank).join(' · ')}
              aria-expanded={shown}
              aria-controls={shown ? panelId : undefined}
              aria-label={`${DIRECTION[rank.direction]}: ${STANDING[standing].label}. ${detailsOf(
                rank,
              ).join('. ')}`}
              onClick={() => {
                setOpen(shown ? null : rank.direction)
              }}
            >
              <Icon name={STANDING[standing].icon} small />
              <span>{rank.direction === 'reverse' ? '←' : '→'}</span>
            </button>
          )
        })}
      </div>
      {open !== null && (
        <ul className="card-standing__details" id={panelId}>
          {detailsOf(ranks.find((rank) => rank.direction === open)!).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** What the colour stands for, spelled out: the numbers first, then why MyLib says what it says. */
function detailsOf(rank: LearningRank): string[] {
  const direction = DIRECTION[rank.direction]
  const numbers =
    rank.assessedAttempts === 0
      ? `${direction}: not asked yet`
      : `${direction}: progress ${rank.learningProgress} of 100, missed ${rank.incorrectAttempts} of ${rank.assessedAttempts}`
  return [numbers, ...rank.toughReasons.map((reason) => TOUGH_REASON_COPY[reason])]
}
